import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  Fragment,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  createBatchAwareSpeed,
  useSmoothedContent,
} from '@/components/ui/smoothdown/useSmoothedContent';
import { classList } from '@/utils/cn';

import { CorrectableTranscriptText } from './meetings-transcript-correctable-text';
import {
  getVisibleWordCount,
  tokenizeWords,
  type TranscriptWordSelection,
} from './meetings-transcript-display.utils';
import { joinSegmentText, type LineSegment } from './meetings-transcript.utils';

function interimTextClass(isOthers: boolean) {
  return isOthers
    ? 'text-foreground-muted'
    : 'text-[color-mix(in_srgb,var(--foreground-default)_65%,transparent)]';
}

export function TranscriptBubbleText({
  segments,
  isOthers,
  isLive = false,
  onSelectWord,
}: {
  segments: LineSegment[];
  isOthers: boolean;
  isLive?: boolean;
  onSelectWord?: (selection: TranscriptWordSelection) => void;
}) {
  const wasLiveRef = useRef(isLive);
  const [showExitingDots, setShowExitingDots] = useState(false);

  useEffect(() => {
    const wasLive = wasLiveRef.current;
    wasLiveRef.current = isLive;
    if (!wasLive || isLive) return;

    setShowExitingDots(true);
    const timeout = setTimeout(() => setShowExitingDots(false), 300);
    return () => clearTimeout(timeout);
  }, [isLive]);

  if (isLive) {
    return (
      <AnimatedContentHeight>
        <LiveTranscriptBubbleText segments={segments} isOthers={isOthers} />
      </AnimatedContentHeight>
    );
  }
  return (
    <>
      {onSelectWord && segments.every((segment) => segment.isFinal) ? (
        <CorrectableTranscriptText
          text={joinSegmentText(segments)}
          onSelectWord={onSelectWord}
        />
      ) : (
        segments.map((segment, index) => {
          const text = (index > 0 ? ' ' : '') + segment.text;
          return segment.isFinal ? (
            <Fragment key={index}>{text}</Fragment>
          ) : (
            <span key={index} className={interimTextClass(isOthers)}>
              {text}
            </span>
          );
        })
      )}
      {showExitingDots && <LiveTranscriptDots isOthers={isOthers} exiting />}
    </>
  );
}

function AnimatedContentHeight({ children }: { children: ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const applyHeight = () => {
      outer.style.height = `${inner.offsetHeight}px`;
    };
    applyHeight();
    const observer = new ResizeObserver(applyHeight);
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={outerRef}
      className="overflow-hidden motion-safe:transition-[height] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.23,1,0.32,1)]"
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}

const FINALIZE_STAGGER_MS = 40;
const FINALIZE_STAGGER_MAX_MS = 320;

function LiveTranscriptBubbleText({
  segments,
  isOthers,
}: {
  segments: LineSegment[];
  isOthers: boolean;
}) {
  const fullText = joinSegmentText(segments);

  const mountTextLength = useRef(fullText.length).current;

  let finalPrefixLength = 0;
  for (const segment of segments) {
    if (!segment.isFinal) break;
    finalPrefixLength += (finalPrefixLength > 0 ? 1 : 0) + segment.text.length;
  }

  const prevFinalPrefixRef = useRef(finalPrefixLength);
  useEffect(() => {
    prevFinalPrefixRef.current = finalPrefixLength;
  });

  const speed = useMemo(
    () =>
      createBatchAwareSpeed({
        batchWindowSec: 1.2,
        minWindowMs: 900,
        maxWindowMs: 2200,
        emaAlpha: 0.2,
        jitterMarginMs: 140,
        minCharactersPerFrame: 0.08,
        maxCharactersPerFrame: 8,
        reserveCharacters: 12,
        resetGapMs: 3000,
      }),
    []
  );
  const smoothed = useSmoothedContent(fullText, {
    adoptInitialContent: true,
    variableCharactersPerFrame: speed,
  });

  const words = useMemo(() => tokenizeWords(fullText), [fullText]);
  const visibleWordCountRef = useRef(0);
  const visibleWordCount = getVisibleWordCount(
    words,
    smoothed.length,
    visibleWordCountRef.current
  );
  useEffect(() => {
    visibleWordCountRef.current = visibleWordCount;
  });

  let newlyFinalCount = 0;
  return (
    <>
      {words.map((word, index) => {
        if (index >= visibleWordCount) return null;
        const isInterim = word.start >= finalPrefixLength;
        const isNewlyFinal =
          !isInterim && word.start >= prevFinalPrefixRef.current;
        const staggerDelayMs = isNewlyFinal
          ? Math.min(
              newlyFinalCount++ * FINALIZE_STAGGER_MS,
              FINALIZE_STAGGER_MAX_MS
            )
          : undefined;
        return (
          <Fragment key={index}>
            {word.leading}
            <span
              className={classList(
                'inline-block',
                word.start >= mountTextLength &&
                  'motion-safe:animate-transcript-word-in',
                'transition-colors duration-300',
                isInterim && interimTextClass(isOthers)
              )}
              style={
                staggerDelayMs !== undefined
                  ? { transitionDelay: `${staggerDelayMs}ms` }
                  : undefined
              }
            >
              {word.core}
            </span>
            {word.trailing}
          </Fragment>
        );
      })}
      <LiveTranscriptDots isOthers={isOthers} />
    </>
  );
}

const DOT_INDICES = [0, 1, 2];

function LiveTranscriptDots({
  isOthers,
  exiting = false,
}: {
  isOthers: boolean;
  exiting?: boolean;
}) {
  return (
    <span
      className={classList(
        'inline-flex items-center gap-0.5 ml-1.5 align-middle origin-left',
        interimTextClass(isOthers),
        exiting
          ? 'overflow-hidden motion-safe:animate-transcript-dots-out motion-reduce:hidden'
          : 'motion-safe:animate-transcript-dots-in'
      )}
      style={{ opacity: 'calc(0.55 + var(--live-energy, 1) * 0.45)' }}
    >
      {DOT_INDICES.map((dotIndex) => (
        <span
          key={dotIndex}
          className="size-1 rounded-full bg-current motion-safe:animate-typing-dot inline-block"
          style={{
            animationDuration: '1.6s',
            animationDelay: `${dotIndex * 0.25 - 1.6}s`,
          }}
        />
      ))}
    </span>
  );
}

export function JumpToLivePill({
  show,
  newLineCount,
  onClick,
}: {
  show: boolean;
  newLineCount: number;
  onClick: () => void;
}) {
  const shouldReduceMotion = useReducedMotion();
  const hidden = {
    opacity: 0,
    y: shouldReduceMotion ? 0 : 8,
    scale: shouldReduceMotion ? 1 : 0.95,
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={hidden}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={hidden}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="absolute bottom-3 inset-x-0 z-10 flex justify-center pointer-events-none"
        >
          <button
            type="button"
            onClick={onClick}
            className={classList(
              'pointer-events-auto flex items-center gap-1 no-record',
              'rounded-full pl-2 pr-2.5 py-1 text-[11px] font-medium',
              'bg-[color-mix(in_srgb,var(--surface-default)_82%,transparent)]',
              'border border-grayA-3 shadow-md backdrop-blur-xl',
              'text-foreground',
              'transition-[color,transform] duration-150 active:scale-[0.97]'
            )}
          >
            <Icons.arrowDown className="size-3" />
            <span
              key={newLineCount}
              className="motion-safe:animate-transcript-word-in"
            >
              {newLineCount > 0 ? `${newLineCount} new` : 'Jump to live'}
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function TranscriptConsentNotice({ className }: { className?: string }) {
  return (
    <div
      className={classList(
        'text-sm text-foreground-muted no-record',
        className
      )}
    >
      Always get consent when transcribing others.
    </div>
  );
}
